import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from '../../i18n';
import FieldRead from '../FieldRead';
import Loading from '../Loading';
import { getOnboardingProgress, OnboardingBackNavigation, OnboardingFrame, OnboardingHeader, OnboardingPrimaryButton, OnboardingProgress, OnboardingSecurityFooter, OnboardingShell, } from '../onboarding-visual';
import { ActionArea, ConsentError, ConsentField, ConsentInput, ConsentLabel, ConsentText, ContentCard, ContentGroup, Introduction, Metadata, ScrollArea, TermsContent, StepIndicator, TermsMain, Title, } from './termsStyles';
var toInteger = function (value) {
    var parsedValue = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : NaN;
    if (!isFinite(parsedValue) || Math.floor(parsedValue) !== parsedValue)
        return undefined;
    return parsedValue;
};
var toPositiveInteger = function (value) {
    var parsedValue = toInteger(value);
    return parsedValue && parsedValue > 0 ? parsedValue : undefined;
};
var getStepFromIndex = function (value) {
    var stepIndex = toInteger(value);
    return stepIndex !== undefined && stepIndex >= 0 ? stepIndex + 1 : undefined;
};
/**
 * `countSteps` remains the source of truth. This visual fallback only accepts
 * a complete, contiguous business-step list, so a partial form never invents a total.
 */
var getCompleteStepsCount = function (steps) {
    if (!Array.isArray(steps) || !steps.length)
        return undefined;
    var formSteps = steps;
    var stepNumbers = [];
    for (var index = 0; index < formSteps.length; index += 1) {
        var item = formSteps[index];
        if (!item || typeof item !== 'object')
            return undefined;
        var step = toPositiveInteger(item.step);
        if (!step)
            return undefined;
        stepNumbers.push(step);
    }
    return stepNumbers
        .slice()
        .sort(function (left, right) { return left - right; })
        .every(function (step, index) { return step === index + 1; })
        ? stepNumbers.length
        : undefined;
};
var getStepFromCompleteList = function (steps, formStep, completeStepsCount) {
    if (!completeStepsCount || !Array.isArray(steps))
        return undefined;
    var stepIndex = steps.indexOf(formStep);
    return stepIndex >= 0 ? stepIndex + 1 : undefined;
};
var getTermsProgress = function (form, formStep) {
    var configuredProgress = getOnboardingProgress(form, formStep);
    if (configuredProgress)
        return configuredProgress;
    var progressForm = form;
    var progressStep = formStep;
    var completeStepsCount = getCompleteStepsCount(progressForm.steps);
    var totalSteps = toPositiveInteger(progressForm.countSteps) || completeStepsCount;
    var currentStep = toPositiveInteger(progressStep.step) ||
        toPositiveInteger(progressStep.currentStep) ||
        toPositiveInteger(progressForm.currentStep) ||
        getStepFromIndex(progressStep.stepIndex) ||
        getStepFromIndex(progressForm.stepIndex) ||
        getStepFromCompleteList(progressForm.steps, formStep, completeStepsCount);
    if (!currentStep || !totalSteps)
        return null;
    return getOnboardingProgress({ countSteps: totalSteps }, { step: currentStep });
};
var TermsConsent = function (_a) {
    var formStep = _a.formStep, readFields = _a.readFields, textFields = _a.textFields, checkboxFields = _a.checkboxFields, enableButton = _a.enableButton, loading = _a.loading, onChangeCheckbox = _a.onChangeCheckbox, onPressConfirm = _a.onPressConfirm;
    var _b = useSelector(function (state) { return state.form; }), form = _b.form, progressForm = _b.progressForm;
    var theme = useSelector(function (state) { return state.theme; }).theme;
    var t = useTranslation().t;
    var progress = getTermsProgress(progressForm || form, formStep);
    var title = t('termsConsent.title');
    var introductoryText = t('termsConsent.subtitle');
    return (React.createElement(OnboardingShell, null,
        React.createElement(OnboardingFrame, null,
            React.createElement(OnboardingHeader, { logo: theme.logo, hideLogo: theme.logoPosition === 'none' }),
            React.createElement(OnboardingProgress, { currentStep: progress === null || progress === void 0 ? void 0 : progress.currentStep, showLabel: false, totalSteps: progress === null || progress === void 0 ? void 0 : progress.totalSteps }),
            React.createElement(TermsMain, null,
                React.createElement(TermsContent, null,
                    progress && (React.createElement(StepIndicator, null, t('termsConsent.progress', { current: progress.currentStep, total: progress.totalSteps }))),
                    React.createElement(Title, { id: "terms-consent-title" }, title),
                    React.createElement(Introduction, null, introductoryText),
                    textFields.map(function (_a) {
                        var field = _a.field, index = _a.index;
                        return (React.createElement(Metadata, { key: "text-".concat(index) },
                            field.label,
                            " ",
                            field.value));
                    }),
                    React.createElement(ContentCard, { "aria-labelledby": "terms-consent-title" },
                        React.createElement(ScrollArea, { "aria-labelledby": "terms-consent-title", role: "region", tabIndex: 0 }, readFields.map(function (_a) {
                            var field = _a.field, index = _a.index;
                            return (React.createElement(ContentGroup, { key: "read-".concat(index) },
                                React.createElement(FieldRead, { field: field })));
                        }))),
                    checkboxFields.map(function (_a) {
                        var field = _a.field, index = _a.index;
                        var inputId = "terms-consent-".concat(index);
                        var errorId = "".concat(inputId, "-error");
                        var errorMessage = field.valid === false && field.msg ? field.msg : undefined;
                        return (React.createElement(ConsentField, { key: "checkbox-".concat(index) },
                            React.createElement(ConsentLabel, { htmlFor: inputId },
                                React.createElement(ConsentInput, { "aria-describedby": errorMessage ? errorId : undefined, "aria-invalid": errorMessage ? true : undefined, "aria-required": field.required || undefined, checked: field.value === 'true', id: inputId, type: "checkbox", onChange: function (event) {
                                        return onChangeCheckbox(index, event.target.checked ? 'true' : 'false');
                                    } }),
                                React.createElement(ConsentText, null, field.label)),
                            errorMessage && (React.createElement(ConsentError, { id: errorId, role: "alert" }, errorMessage))));
                    })),
                React.createElement(ActionArea, null,
                    React.createElement(OnboardingPrimaryButton, { ariaBusy: loading, disabled: !enableButton || loading, onClick: onPressConfirm }, t('common.continue')),
                    React.createElement(OnboardingBackNavigation, { label: t('common.back'), show: formStep.goBack }),
                    React.createElement(OnboardingSecurityFooter, { text: t('common.secureEnvironment') }))),
            loading && React.createElement(Loading, null))));
};
export default TermsConsent;
